import { useState, useCallback, useMemo } from 'react';
import { useReadContract, useWriteContract, useAccount, useChainId, useSwitchChain } from 'wagmi';
import { waitForTransactionReceipt } from '@wagmi/core';
import { type Address, formatUnits } from 'viem';
import { useWallets } from '@privy-io/react-auth';
import { wagmiConfig } from '@/lib/wagmi';
import {
  ESCROW_ABI,
  ESCROW_CONTRACT_ADDRESS,
  ESCROW_CHAIN_ID,
  ERC20_ABI,
  USDC_ADDRESSES,
} from '@/lib/contracts';
import type { Wager } from '@/lib/api';

// ============================================
// Read hooks
// ============================================

/**
 * Read the required deposit (stake + fee) for a wager from the contract.
 */
export function useRequiredDeposit(escrowWagerId: `0x${string}` | undefined) {
  const { data, isLoading, error } = useReadContract({
    address: ESCROW_CONTRACT_ADDRESS,
    abi: ESCROW_ABI,
    functionName: 'calculateRequiredDeposit',
    args: escrowWagerId ? [escrowWagerId] : undefined,
    chainId: ESCROW_CHAIN_ID,
    query: { enabled: !!escrowWagerId },
  });

  return {
    requiredDeposit: data as bigint | undefined,
    isLoading,
    error,
  };
}

/**
 * Check if a player has funded a wager on-chain.
 */
export function useHasPlayerFunded(
  escrowWagerId: `0x${string}` | undefined,
  playerAddress: Address | undefined
) {
  const { data, isLoading } = useReadContract({
    address: ESCROW_CONTRACT_ADDRESS,
    abi: ESCROW_ABI,
    functionName: 'hasPlayerFunded',
    args: escrowWagerId && playerAddress ? [escrowWagerId, playerAddress] : undefined,
    chainId: ESCROW_CHAIN_ID,
    query: { enabled: !!escrowWagerId && !!playerAddress },
  });

  return { hasFunded: data as boolean | undefined, isLoading };
}

/**
 * Read the connected wallet's USDC balance on the escrow chain.
 * Always reads from ESCROW_CHAIN_ID regardless of what chain the wallet
 * is currently on, since funding happens on the escrow chain.
 */
export function useUSDCBalance() {
  const { address } = useAccount();
  const usdcAddress = USDC_ADDRESSES[ESCROW_CHAIN_ID];

  const { data: rawBalance, isLoading: isLoadingBalance } = useReadContract({
    address: usdcAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: ESCROW_CHAIN_ID,
    query: { enabled: !!address && !!usdcAddress },
  });

  const { data: decimals } = useReadContract({
    address: usdcAddress,
    abi: ERC20_ABI,
    functionName: 'decimals',
    chainId: ESCROW_CHAIN_ID,
    query: { enabled: !!usdcAddress },
  });

  const balance = rawBalance != null && decimals != null
    ? formatUnits(rawBalance, decimals)
    : undefined;

  return { balance, rawBalance, decimals, isLoadingBalance };
}

// ============================================
// Funding step enum
// ============================================

export type FundingStep =
  | 'idle'
  | 'switching_chain'
  | 'approving'
  | 'waiting_approval'
  | 'funding'
  | 'waiting_confirmation'
  | 'reporting'
  | 'success'
  | 'error';

const STEP_LABELS: Record<FundingStep, string> = {
  idle: '',
  switching_chain: 'Switching network...',
  approving: 'Approve USDC spend...',
  waiting_approval: 'Waiting for approval confirmation...',
  funding: 'Sign fund transaction...',
  waiting_confirmation: 'Waiting for on-chain confirmation...',
  reporting: 'Recording on backend...',
  success: 'Funded successfully!',
  error: 'Transaction failed',
};

export { STEP_LABELS as FUNDING_STEP_LABELS };

// ============================================
// Main orchestration hook
// ============================================

interface UseFundEscrowOptions {
  wager: Wager;
  onSuccess?: (txHash: string) => void;
  onError?: (err: Error) => void;
}

// ============================================
// Collection step enum
// ============================================

export type CollectStep =
  | 'idle'
  | 'switching_chain'
  | 'collecting'
  | 'waiting_confirmation'
  | 'reporting'
  | 'success'
  | 'error';

const COLLECT_STEP_LABELS: Record<CollectStep, string> = {
  idle: '',
  switching_chain: 'Switching network...',
  collecting: 'Sign collect transaction...',
  waiting_confirmation: 'Waiting for on-chain confirmation...',
  reporting: 'Recording on backend...',
  success: 'Winnings collected!',
  error: 'Transaction failed',
};

export { COLLECT_STEP_LABELS };

// ============================================
// Collect winnings hook
// ============================================

interface UseCollectWinningsOptions {
  wager: Wager;
  onSuccess?: (txHash: string) => void;
  onError?: (err: Error) => void;
}

export function useCollectWinnings({ wager, onSuccess, onError }: UseCollectWinningsOptions) {
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const [step, setStep] = useState<CollectStep>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const escrowWagerId = wager.escrowWagerId as `0x${string}` | undefined;
  const isProcessing = step !== 'idle' && step !== 'success' && step !== 'error';

  const collect = useCallback(async () => {
    if (!address || !escrowWagerId) return;

    setErrorMsg(null);

    try {
      // 1. Switch chain if needed
      if (chainId !== ESCROW_CHAIN_ID) {
        setStep('switching_chain');
        await switchChainAsync({ chainId: ESCROW_CHAIN_ID });
      }

      // 2. Call collectWinnings on the escrow contract
      setStep('collecting');
      const txHash = await writeContractAsync({
        address: ESCROW_CONTRACT_ADDRESS,
        abi: ESCROW_ABI,
        functionName: 'collectWinnings',
        args: [escrowWagerId],
        chainId: ESCROW_CHAIN_ID,
      });

      // 3. Wait for confirmation
      setStep('waiting_confirmation');
      await waitForTransactionReceipt(wagmiConfig, {
        hash: txHash,
        chainId: ESCROW_CHAIN_ID,
      });

      setStep('reporting');
      onSuccess?.(txHash);
      setStep('success');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setErrorMsg(error.message);
      setStep('error');
      onError?.(error);
    }
  }, [address, escrowWagerId, chainId, switchChainAsync, writeContractAsync, onSuccess, onError]);

  const reset = useCallback(() => {
    setStep('idle');
    setErrorMsg(null);
  }, []);

  return {
    collect,
    reset,
    step,
    stepLabel: COLLECT_STEP_LABELS[step],
    errorMsg,
    isProcessing,
  };
}

// ============================================
// Main funding orchestration hook
// ============================================

export function useFundEscrow({ wager, onSuccess, onError }: UseFundEscrowOptions) {
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const { wallets } = useWallets();

  // Verify the active wagmi account is the Privy embedded wallet
  const embeddedAddress = useMemo(() => {
    const embedded = wallets.find((w) => w.walletClientType === 'privy');
    return embedded?.address?.toLowerCase();
  }, [wallets]);

  const [step, setStep] = useState<FundingStep>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fundTxHash, setFundTxHash] = useState<string | null>(null);

  const escrowWagerId = wager.escrowWagerId as `0x${string}` | undefined;
  const isETH = wager.currency === 'ETH';
  const usdcAddress = chainId ? USDC_ADDRESSES[chainId] : undefined;

  const { requiredDeposit, isLoading: isLoadingDeposit } = useRequiredDeposit(escrowWagerId);

  const isProcessing = step !== 'idle' && step !== 'success' && step !== 'error';
  const needsChainSwitch = chainId !== ESCROW_CHAIN_ID;

  const fund = useCallback(async () => {
    if (!address || !escrowWagerId || requiredDeposit == null) return;

    // Safety: ensure the active wagmi account is the Privy embedded wallet
    if (embeddedAddress && address.toLowerCase() !== embeddedAddress) {
      const err = new Error('Wallet mismatch: please use your PullUp embedded wallet to fund.');
      setErrorMsg(err.message);
      setStep('error');
      onError?.(err);
      return;
    }

    setErrorMsg(null);
    setFundTxHash(null);

    try {
      // 1. Switch chain if needed
      if (chainId !== ESCROW_CHAIN_ID) {
        setStep('switching_chain');
        await switchChainAsync({ chainId: ESCROW_CHAIN_ID });
      }

      // 2. For USDC: check allowance and approve if needed
      if (!isETH && usdcAddress) {
        setStep('approving');
        const approveTxHash = await writeContractAsync({
          address: usdcAddress,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [ESCROW_CONTRACT_ADDRESS, requiredDeposit],
          chainId: ESCROW_CHAIN_ID,
        });

        setStep('waiting_approval');
        await waitForTransactionReceipt(wagmiConfig, {
          hash: approveTxHash,
          chainId: ESCROW_CHAIN_ID,
        });
      }

      // 3. Call fundWager on the escrow contract
      setStep('funding');
      const txHash = await writeContractAsync({
        address: ESCROW_CONTRACT_ADDRESS,
        abi: ESCROW_ABI,
        functionName: 'fundWager',
        args: [escrowWagerId],
        chainId: ESCROW_CHAIN_ID,
        // For ETH wagers, send the required deposit as value
        ...(isETH ? { value: requiredDeposit } : {}),
      });

      // 4. Wait for confirmation
      setStep('waiting_confirmation');
      await waitForTransactionReceipt(wagmiConfig, {
        hash: txHash,
        chainId: ESCROW_CHAIN_ID,
      });

      setFundTxHash(txHash);
      setStep('reporting');

      // 5. Notify caller to report to backend
      onSuccess?.(txHash);
      setStep('success');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setErrorMsg(error.message);
      setStep('error');
      onError?.(error);
    }
  }, [
    address,
    embeddedAddress,
    escrowWagerId,
    requiredDeposit,
    chainId,
    isETH,
    usdcAddress,
    switchChainAsync,
    writeContractAsync,
    onSuccess,
    onError,
  ]);

  const reset = useCallback(() => {
    setStep('idle');
    setErrorMsg(null);
    setFundTxHash(null);
  }, []);

  return {
    fund,
    reset,
    step,
    errorMsg,
    requiredDeposit,
    isLoadingDeposit,
    isProcessing,
    needsChainSwitch,
    fundTxHash,
  };
}
